"use client";

import MarketHero from "./MarketHero";
import { ChartWithExpiry } from "./SourceAwareChart";
import dynamic from "next/dynamic";
import { useT } from "@/hooks/useT";

function CryptoChartSkeleton() {
  const t = useT();
  return (
    <div className="h-[320px] md:h-[380px] flex items-center justify-center bg-white dark:bg-[#18181D]">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-2 border-gray-300 dark:border-gray-600 border-t-gray-500 dark:border-t-gray-400 rounded-full animate-spin" />
        <span className="text-sm font-satoshi text-gray-500 dark:text-gray-400">{t("crypto.loadingPriceData")}</span>
      </div>
    </div>
  );
}

const CryptoPriceChart = dynamic(() => import("./CryptoPriceChart"), {
  ssr: false,
  loading: () => <CryptoChartSkeleton />,
});

interface HeroData {
  slug: string;
  question: string;
  image: string | null;
  endDate: string | null;
  closed: boolean;
  active: boolean;
  acceptingOrders: boolean;
  outcomes: string[];
  outcomePrices: number[];
  volume: number;
  volume24hr: number;
  liquidity: number;
  oneDayPriceChange: number | null;
  parentEventSlug?: string | null;
  competitive: number | null;
  conditionId?: string | null;
  eventCategory?: string | null;
}

interface MarketData extends HeroData {
  eventStartTime?: string | null;
  seriesSlug?: string | null;
}

function detectCryptoSeriesPrefix(question: string): string | null {
  const q = question.toLowerCase();
  if (q.includes("bitcoin") || q.includes("btc")) return "btc-up-or-down";
  if (q.includes("ethereum") || q.includes("eth")) return "eth-up-or-down";
  if (q.includes("solana") || q.includes("sol")) return "sol-up-or-down";
  if (q.includes("xrp") || q.includes("ripple")) return "xrp-up-or-down";
  if (q.includes("hyperliquid") || q.includes("hype")) return "hype-up-or-down";
  if (q.includes("doge") || q.includes("dogecoin")) return "doge-up-or-down";
  if (q.includes("bnb") || q.includes("binance coin")) return "bnb-up-or-down";
  return null;
}

export default function PolymarketCryptoHeroChart({
  marketData,
  heroData,
  umaDispute = null,
  marketEnded,
  showClosedBanner = true,
  heroPaddingClass = "px-3 md:px-4 pt-3 pb-0",
}: {
  marketData: MarketData & { priceToBeat: number | null | undefined };
  heroData: HeroData;
  umaDispute?: { state: string } | null;
  marketEnded: boolean;
  showClosedBanner?: boolean;
  heroPaddingClass?: string;
}) {
  const marketSlug = marketData.slug || "";
  const interval =
    marketSlug.includes("-5m-") ? "5m" :
    marketSlug.includes("-15m-") ? "15m" :
    marketSlug.includes("-1h-") ? "1h" :
    marketSlug.includes("-4h-") ? "4h" :
    (marketData.eventStartTime && marketData.endDate)
      ? (() => {
          const seconds = Math.round((new Date(marketData.endDate).getTime() - new Date(marketData.eventStartTime!).getTime()) / 1000);
          if (seconds === 300) return "5m";
          if (seconds === 900) return "15m";
          if (seconds === 3600) return "1h";
          if (seconds === 14400) return "4h";
          return null;
        })()
      : null;
  const seriesSlug = marketData.seriesSlug || (() => {
    const prefix = detectCryptoSeriesPrefix(marketData.question || heroData.question || "");
    return prefix && interval ? `${prefix}-${interval}` : null;
  })();

  return (
    <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] overflow-hidden">
      <div className={heroPaddingClass}>
        <MarketHero
          marketData={heroData}
          source="polymarket"
          umaDispute={umaDispute}
          showClosedBanner={showClosedBanner}
          hideStats
        />
      </div>
      <div className="border-t border-gray-200 dark:border-[#1A1A1A]">
        <ChartWithExpiry slug={marketData.slug} marketEnded={marketEnded} seriesSlug={seriesSlug}>
          <CryptoPriceChart
            priceToBeat={marketData.priceToBeat}
            eventStartTime={marketData.eventStartTime || null}
            endDate={marketData.endDate}
            question={marketData.question}
            conditionId={marketData.conditionId}
            marketSlug={marketData.slug}
            interval={interval}
            // PM moved hourly + 4hourly BTC up/down to Binance-resolved
            // (resolutionSource = binance.com/.../trade/BTC_USDT). Pull
            // the live tape from the matching feed so the hero matches
            // PM tick-for-tick instead of trailing by $5–$15.
            preferredSource={interval === "1h" || interval === "4h" ? "binance" : "chainlink"}
          />
        </ChartWithExpiry>
      </div>
    </div>
  );
}
